import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  DollarSign, Loader2, Building2, 
  CheckCircle, XCircle, Send,
  AlertTriangle, RefreshCw, ChevronsUpDown, Check,
  Banknote, CreditCard, ArrowDownToLine, ArrowUpFromLine, Percent, Wallet, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { cn } from '../lib/utils';

const FundTransfers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [campaignsPending, setCampaignsPending] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Campaign filter with search
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  
  // Transfer form
  const [transferForm, setTransferForm] = useState({
    amount: '',
    notes: ''
  });
  
  // Complete form
  const [completeForm, setCompleteForm] = useState({
    transaction_reference: '',
    transfer_date: '',
    send_notification: true
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch stats when campaign filter changes
  useEffect(() => {
    if (!loading) {
      fetchStats();
    }
  }, [campaignFilter]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [statsRes, campaignsRes, transfersRes, allCampaignsRes] = await Promise.all([
        api.get('/fund-transfers/stats'),
        api.get('/fund-transfers/campaigns-pending'),
        api.get('/fund-transfers'),
        api.get('/campaigns')
      ]);
      
      setStats(statsRes.data);
      setCampaignsPending(campaignsRes.data);
      setTransfers(transfersRes.data);
      // Filter only kaizen_managed campaigns for the filter dropdown
      const kaizenCampaigns = allCampaignsRes.data.filter(c => c.fund_collection_mode === 'kaizen_managed');
      setAllCampaigns(kaizenCampaigns);
    } catch (error) {
      console.error('Failed to fetch fund transfer data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = campaignFilter !== 'all' ? { campaign_id: campaignFilter } : {};
      const statsRes = await api.get('/fund-transfers/stats', { params });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const refreshData = async () => {
    await fetchInitialData();
    toast.success('Data refreshed');
  };

  // Filter transfers based on selected campaign
  const filteredTransfers = useMemo(() => {
    if (campaignFilter === 'all') {
      return transfers;
    }
    return transfers.filter(t => t.campaign_id === campaignFilter);
  }, [campaignFilter, transfers]);

  // Get selected campaign name for display
  const selectedCampaignName = useMemo(() => {
    if (campaignFilter === 'all') return 'All Events';
    const campaign = allCampaigns.find(c => c.id === campaignFilter);
    return campaign?.name || 'Select Event';
  }, [campaignFilter, allCampaigns]);

  const openTransferModal = (campaign) => {
    setSelectedCampaign(campaign);
    setTransferForm({
      amount: campaign.pending.toFixed(2),
      notes: ''
    });
    setShowTransferModal(true);
  };

  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    if (!selectedCampaign) return;
    
    setSubmitting(true);
    try {
      await api.post('/fund-transfers', {
        campaign_id: selectedCampaign.campaign_id,
        amount: parseFloat(transferForm.amount),
        notes: transferForm.notes
      });
      
      toast.success('Transfer request submitted for approval');
      setShowTransferModal(false);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer request');
    } finally {
      setSubmitting(false);
    }
  };

  const openCompleteModal = (transfer) => {
    setSelectedTransfer(transfer);
    setCompleteForm({
      transaction_reference: '',
      transfer_date: format(new Date(), 'yyyy-MM-dd'),
      send_notification: true
    });
    setShowCompleteModal(true);
  };

  const handleCompleteTransfer = async (e) => {
    e.preventDefault();
    if (!selectedTransfer) return;
    
    setSubmitting(true);
    try {
      await api.put(`/fund-transfers/${selectedTransfer.id}/complete`, {
        transaction_reference: completeForm.transaction_reference,
        transfer_date: completeForm.transfer_date,
        send_notification: completeForm.send_notification
      });
      
      toast.success('Transfer marked as completed');
      setShowCompleteModal(false);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendNotification = async (transfer) => {
    try {
      await api.post(`/fund-transfers/${transfer.id}/send-notification`);
      toast.success(`Notification sent to ${transfer.notification_email}`);
      refreshData();
    } catch (error) {
      toast.error('Failed to send notification');
    }
  };

  const [syncing, setSyncing] = useState(false);

  const syncSettlements = async () => {
    try {
      setSyncing(true);
      await api.post('/fund-transfers/sync-all-settlements');
      toast.success('Settlement data synced from payment gateways');
      await fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync settlements');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending Approval' },
      approved: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' }
    };
    const badge = badges[status] || badges.pending_approval;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900">Fund Transfers</h1>
              <p className="text-sm text-slate-500 mt-1">Gateway fees are synced automatically when payments are verified</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={syncSettlements} 
                variant="outline" 
                size="sm"
                disabled={syncing}
                title="Manually sync settlement data for old payments"
              >
                {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
                Re-sync
              </Button>
              <Button onClick={refreshData} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Campaign Filter with Search */}
        <div className="mb-6">
          <Label className="text-sm text-slate-600 mb-2 block">Filter by Event</Label>
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={filterOpen}
                className="w-full md:w-[400px] justify-between bg-white"
              >
                <span className="truncate">{selectedCampaignName}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full md:w-[400px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search events..." />
                <CommandList>
                  <CommandEmpty>No event found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => {
                        setCampaignFilter('all');
                        setFilterOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          campaignFilter === 'all' ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="font-medium">All Events</span>
                    </CommandItem>
                    {allCampaigns.map((campaign) => (
                      <CommandItem
                        key={campaign.id}
                        value={campaign.name}
                        onSelect={() => {
                          setCampaignFilter(campaign.id);
                          setFilterOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            campaignFilter === campaign.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <span>{campaign.name}</span>
                          <span className="text-xs text-slate-500">{campaign.organization_name}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Stats Cards - 8 cards in 2 rows */}
        {stats && (
          <div className="space-y-4 mb-8">
            {/* Row 1: Payment Collection & Gateway */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-white p-5 border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Banknote className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-500">Total Collected</p>
                </div>
                <p className="text-2xl font-bold text-slate-900">₹{stats.total_collected?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-400 mt-1">Amount users paid</p>
              </Card>
              
              <Card className="bg-white p-5 border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-red-600" />
                  </div>
                  <p className="text-sm text-slate-500">Gateway Charges</p>
                </div>
                <p className="text-2xl font-bold text-red-600">₹{stats.gateway_charges?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || 0}</p>
                <p className="text-xs text-slate-400 mt-1">Payment gateway fee</p>
              </Card>
              
              <Card className="bg-green-50 p-5 border-green-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <ArrowDownToLine className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-sm text-green-700">Gateway Settled</p>
                </div>
                <p className="text-2xl font-bold text-green-700">₹{stats.gateway_settled?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || 0}</p>
                <p className="text-xs text-green-600 mt-1">Received in Kaizen's account</p>
              </Card>
              
              <Card className="bg-yellow-50 p-5 border-yellow-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-yellow-600" />
                  </div>
                  <p className="text-sm text-yellow-700">Gateway Pending</p>
                </div>
                <p className="text-2xl font-bold text-yellow-700">₹{stats.gateway_pending?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || 0}</p>
                <p className="text-xs text-yellow-600 mt-1">Yet to be settled by gateway</p>
              </Card>
            </div>

            {/* Row 2: Platform Fee & Organizer Payments */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-violet-50 p-5 border-violet-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Percent className="w-4 h-4 text-violet-600" />
                  </div>
                  <p className="text-sm text-violet-700">Platform Fee (2.95%)</p>
                </div>
                <p className="text-2xl font-bold text-violet-700">₹{stats.platform_fee?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || 0}</p>
                <p className="text-xs text-violet-600 mt-1">Kaizen platform charges</p>
              </Card>
              
              {/* Kaizen's Share - Only visible to Directors */}
              {user?.can_approve_transfers && (
                <Card className="bg-indigo-50 p-5 border-indigo-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-indigo-600" />
                    </div>
                    <p className="text-sm text-indigo-700">Kaizen's Share</p>
                  </div>
                  <p className="text-2xl font-bold text-indigo-700">₹{stats.kaizen_share?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || 0}</p>
                  <p className="text-xs text-indigo-600 mt-1">Platform fee - Gateway charges</p>
                </Card>
              )}
              
              <Card className="bg-white p-5 border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-slate-600" />
                  </div>
                  <p className="text-sm text-slate-500">Net Payable</p>
                </div>
                <p className="text-2xl font-bold text-slate-900">₹{stats.net_payable?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || 0}</p>
                <p className="text-xs text-slate-400 mt-1">Settled - Kaizen's share</p>
              </Card>
              
              <Card className="bg-green-50 p-5 border-green-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <ArrowUpFromLine className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-sm text-green-700">Paid to Organizer</p>
                </div>
                <p className="text-2xl font-bold text-green-700">₹{stats.total_transferred?.toLocaleString() || 0}</p>
                <p className="text-xs text-green-600 mt-1">Transferred to organizer</p>
              </Card>
              
              <Card className="bg-orange-50 p-5 border-orange-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-orange-600" />
                  </div>
                  <p className="text-sm text-orange-700">Pending to Organizer</p>
                </div>
                <p className="text-2xl font-bold text-orange-700">₹{stats.pending_transfer?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || 0}</p>
                <p className="text-xs text-orange-600 mt-1">Yet to transfer</p>
              </Card>
            </div>
          </div>
        )}

        {/* Campaigns with Pending Transfers */}
        <Card className="bg-white border-slate-200 mb-8">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Events with Pending Transfers</h2>
            <p className="text-sm text-slate-500">Events using Kaizen-managed fund collection</p>
          </div>
          
          {campaignsPending.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-slate-600">All transfers are up to date!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Event</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Collected</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Gateway Settled</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Platform Fee</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Net Payable</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Paid</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Pending</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {campaignsPending.map((campaign) => (
                    <tr key={campaign.campaign_id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{campaign.campaign_name}</p>
                          <p className="text-sm text-slate-500">{campaign.organization_name}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-slate-900">
                        ₹{campaign.collected.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-green-600">
                        ₹{(campaign.gateway_settled || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-violet-600">
                        ₹{campaign.platform_fee.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-slate-900">
                        ₹{campaign.net_payable.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-green-600">
                        ₹{campaign.transferred.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-orange-600">
                        ₹{campaign.pending.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Button
                          size="sm"
                          onClick={() => openTransferModal(campaign)}
                          className="bg-violet-600 hover:bg-violet-700"
                        >
                          Create Transfer
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Recent Transfers */}
        <Card className="bg-white border-slate-200">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Transfer History</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {campaignFilter === 'all' 
                    ? 'All transfer requests and their status' 
                    : `Transfers for ${selectedCampaignName}`}
                </p>
              </div>
              {stats?.pending_approval_count > 0 && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                  {stats.pending_approval_count} pending approval
                </span>
              )}
            </div>
          </div>
          
          {filteredTransfers.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">
                {campaignFilter === 'all' ? 'No transfers yet' : 'No transfers for selected event'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Request #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Event</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Reference</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredTransfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-mono text-slate-600">
                        {transfer.request_number}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{transfer.campaign_name}</p>
                          <p className="text-xs text-slate-500">{transfer.organization_name}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                        ₹{transfer.amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getStatusBadge(transfer.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {transfer.transaction_reference || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {format(parseISO(transfer.created_at), 'dd MMM yyyy')}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {transfer.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openCompleteModal(transfer)}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Complete
                            </Button>
                          )}
                          {transfer.status === 'completed' && !transfer.notification_sent && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendNotification(transfer)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Notify
                            </Button>
                          )}
                          {transfer.status === 'rejected' && (
                            <span className="text-xs text-red-500">{transfer.rejection_reason}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Create Transfer Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Transfer Request</DialogTitle>
            <DialogDescription>
              {selectedCampaign && (
                <span>{selectedCampaign.campaign_name} - {selectedCampaign.organization_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTransfer} className="space-y-4">
            {selectedCampaign && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Available for Transfer:</span>
                  <span className="font-semibold text-slate-900">₹{selectedCampaign.pending.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}
            
            <div>
              <Label>Transfer Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                <Input
                  type="number"
                  step="0.01"
                  value={transferForm.amount}
                  onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                  className="pl-8"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={transferForm.notes}
                onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                placeholder="Settlement for registrations till..."
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">
                This request will be sent to a Director for approval before processing.
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowTransferModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-violet-600 hover:bg-violet-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit for Approval
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete Transfer Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Complete Transfer</DialogTitle>
            <DialogDescription>
              {selectedTransfer && (
                <span>Request #{selectedTransfer.request_number} - ₹{selectedTransfer.amount.toLocaleString()}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCompleteTransfer} className="space-y-4">
            {selectedTransfer?.bank_account_snapshot && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-900 mb-2">Transfer To:</p>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>{selectedTransfer.bank_account_snapshot.account_name}</p>
                  <p>{selectedTransfer.bank_account_snapshot.account_number_masked} | {selectedTransfer.bank_account_snapshot.bank_name}</p>
                  <p>IFSC: {selectedTransfer.bank_account_snapshot.ifsc_code}</p>
                </div>
              </div>
            )}
            
            <div>
              <Label>Transaction Reference *</Label>
              <Input
                value={completeForm.transaction_reference}
                onChange={(e) => setCompleteForm({ ...completeForm, transaction_reference: e.target.value })}
                placeholder="NEFT/IMPS/UPI Reference Number"
                className="mt-1"
                required
              />
            </div>
            
            <div>
              <Label>Transfer Date *</Label>
              <Input
                type="date"
                value={completeForm.transfer_date}
                onChange={(e) => setCompleteForm({ ...completeForm, transfer_date: e.target.value })}
                className="mt-1"
                required
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="send_notification"
                checked={completeForm.send_notification}
                onChange={(e) => setCompleteForm({ ...completeForm, send_notification: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="send_notification" className="text-sm text-slate-600 cursor-pointer">
                Send email notification to organizer ({selectedTransfer?.notification_email})
              </Label>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCompleteModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Mark Completed
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FundTransfers;
